import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { isPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { styled } from '@mui/system';
import PlatformConditional from 'Components/PlatformConditional';
import { convertTrendToIcon } from '../utils';
import type { TImagingDocumentType } from '../utils';
import TileContentMaker from 'Components/DashboardTile/TileContentMaker';
import type { IData } from 'Components/DashboardTile/TileContentMaker/components/definitions';
import { filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';

const Spacer = styled('span')({
  padding: '0 0.5rem',
});

const makeMRIData = (title: IData['title'], value: IData['value'], key: string, icon?: React.JSX.Element | null) => [
  {
    title,
    value: icon ? (
      <span>
        {value}
        <Spacer />
        {icon}
      </span>
    ) : (
      value
    ),
    key,
  },
];

const ImagingDash = ({ documents }: IOwnProps): React.JSX.Element | undefined => {
  const docs = filterDocumentsByType(documents, [
    'mri',
    'scintigraphy',
    'thymusCt',
    'thymusMri',
    'echocardiography',
    'tt',
    'nerveUltrasound',
  ]).toSorted(sortDocumentsDescending);

  // SMA studies
  const smaMriArr = filterDocumentsByType(docs, ['mri']);
  const smaArr: typeof docs = docs
    .filter(d => d._type !== 'echocardiography')
    .filter(d => d._type !== 'mri')
    .concat(smaMriArr as any);

  // DMD studies
  const dmdArr = docs.filter(d => d._type !== 'mri');

  // MS studies
  const msArr = filterDocumentsByType(docs, ['mri']);

  // Parkinson studies
  const parkinsonMriArr = filterDocumentsByType(docs, ['mri']);
  const parkinsonArr = filterDocumentsByType(docs, ['tt', 'scintigraphy']).concat(parkinsonMriArr as any);

  const ninmtMriArr = filterDocumentsByType(docs, ['mri']);

  // MG studies
  const mgravisArr = filterDocumentsByType(docs, ['thymusCt', 'thymusMri']);

  const polyneuroArr = docs.filter(d => d._type === 'mri' || d._type === 'nerveUltrasound');

  const imagingStudy = (document: (typeof docs)[0]): React.JSX.Element => (
    <React.Fragment>
      <PlatformConditional platform={'ms'}>
        <TileContentMaker
          type="bigHeaderInfo"
          bigHeaderText={<FormattedMessage id={`imaging.${document._type}`} />}
          bigHeaderValue={isPartialDate(document.date) ? formatPartialDate(document.date) : ''}
          data={[
            ...('tesla' in document
              ? makeMRIData(<FormattedMessage id={'imaging.tesla'} />, document.tesla, 'tesla')
              : []),
            ...('brainT2Flair' in document
              ? makeMRIData(
                  <FormattedMessage id={'imaging.brainT2FlairInfraShort'} />,
                  document.brainT2Flair?.infratentorial?.lesions,
                  'brainT2FlairInfraShort',
                  convertTrendToIcon(document.brainT2Flair?.infratentorial?.lesionsTrend),
                )
              : []),
            ...('brainT2Flair' in document && document.brainT2Flair?.supratentorial
              ? makeMRIData(
                  <FormattedMessage id={'imaging.brainT2FlairSupraShort'} />,
                  document.brainT2Flair.supratentorial.lesions,
                  'brainT2FlairSupraShort',
                  convertTrendToIcon(document.brainT2Flair.supratentorial.lesionsTrend),
                )
              : []),
            ...('brainT1' in document && document.brainT1?.total
              ? makeMRIData(
                  <FormattedMessage id={'imaging.brainT1TotalShort'} />,
                  document.brainT1.total.lesions,
                  'brainT1TotalShort',
                  convertTrendToIcon(document.brainT1.total.lesionsTrend),
                )
              : []),
            ...('brainT1' in document && document.brainT1?.gadoliniumEnhanced
              ? makeMRIData(
                  <FormattedMessage id={'imaging.brainT1GadoliniumShort'} />,
                  document.brainT1.gadoliniumEnhanced.lesions,
                  'brainT1GadoliniumShort',
                  convertTrendToIcon(document.brainT1.gadoliniumEnhanced.lesionsTrend),
                )
              : []),
            ...('spinalCordT2' in document && document.spinalCordT2?.total
              ? makeMRIData(
                  <FormattedMessage id={'imaging.spinalCordT2TotalShort'} />,
                  document.spinalCordT2.total.lesions,
                  'spinalCordT2TotalShort',
                  convertTrendToIcon(document.spinalCordT2.total.lesionsTrend),
                )
              : []),
          ]}
        />
      </PlatformConditional>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <PlatformConditional platform={'sma'}>
        <TileContentMaker
          type="recentList"
          data={smaArr.map(s => ({
            date: formatPartialDate(s.date),
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            key: s._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform={'dmd'}>
        <TileContentMaker
          type="recentList"
          data={dmdArr.map(s => ({
            date: formatPartialDate(s.date),
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            key: s._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform={'ms'}>
        <>{msArr.length > 0 && msArr[0] && imagingStudy(msArr[0])}</>
      </PlatformConditional>
      <PlatformConditional platform={'parkinson'}>
        <TileContentMaker
          type="recentList"
          data={parkinsonArr.map(s => ({
            date: isPartialDate(s.date) ? formatPartialDate(s.date) : '',
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            key: s._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform="ninmt">
        <TileContentMaker
          type="recentList"
          data={ninmtMriArr.map(s => ({
            date: formatPartialDate(s.date),
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            key: s._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform="mgravis">
        <TileContentMaker
          type="recentList"
          data={mgravisArr.map(s => ({
            date: isPartialDate(s.date) ? formatPartialDate(s.date) : '',
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            value: (
              <React.Fragment>
                {Array.isArray(s.finding) && s.finding.length > 0 ? (
                  s.finding.map((f, i, arr) => (
                    <span key={i}>
                      <FormattedMessage id={`imaging.opts.finding.${f}`} />
                      {i < arr.length - 1 ? ', ' : ''}
                    </span>
                  ))
                ) : s.finding ? (
                  <FormattedMessage id={`imaging.opts.finding.${s.finding}`} />
                ) : (
                  '-'
                )}
              </React.Fragment>
            ),
            key: s._id,
          }))}
        />
      </PlatformConditional>
      <PlatformConditional platform="polyneuro">
        <TileContentMaker
          type="recentList"
          data={polyneuroArr.map(s => ({
            date: formatPartialDate(s.date),
            title: <FormattedMessage id={`imaging.${s._type}`} />,
            key: s._id,
          }))}
        />
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<TImagingDocumentType>>;
}

export default ImagingDash;
